import { z } from 'zod';
/**
 * El corpus de PIA · lo que Fundación escribe, revisa, aprueba y publica (E4,
 * PIA-1).
 *
 * **La unidad es la pieza**, no el archivo: Fundación entregó el material del
 * 27/10 como una planilla de 35 piezas, cada una con su ID ("01-001"), su
 * pregunta y su texto. Cada pieza se aprueba sola y se publica sola, y lo que
 * PIA le dice al paciente es el texto de una pieza publicada, tal cual
 * (ADR-026). Nada de lo que vive acá lo escribe el sistema: los textos son de
 * Fundación, letra por letra.
 *
 * Este módulo tiene sólo lo que comparten la api y la PWA: los nombres de los
 * módulos y los estados, la regla de qué estado sigue a cada acción (función
 * pura, con su test) y los contratos de las rutas del panel de Fundación.
 */
/**
 * Los cuatro módulos del material de Fundación, en el orden de la planilla.
 * `reglas_de_pia` (04-001 a 04-011) **no se le responde nunca al paciente**:
 * son instrucciones para PIA, la capa B del prompt de julio. Alimentan el
 * prompt del sistema y quedan fuera de la búsqueda.
 */
export const moduloDelCorpusSchema = z.enum([
    'programa',
    'educacion',
    'acompanamiento',
    'reglas_de_pia',
]);
/**
 * Los cinco estados de una pieza.
 *
 * - `pendiente_de_fundacion`: como llegó; nadie la aprobó. Es el estado de las
 *   35 piezas al cargarlas: aprobar es de Fundación, no nuestro.
 * - `observada`: alguien le encontró un problema (`observacion` dice cuál).
 * - `aprobada`: una persona de Fundación la aprobó con su nombre.
 * - `publicada`: una segunda persona la publicó. Recién ahí PIA la usa.
 * - `retirada`: deja de usarse; el historial se conserva.
 */
export const estadoDePiezaSchema = z.enum([
    'pendiente_de_fundacion',
    'observada',
    'aprobada',
    'publicada',
    'retirada',
]);
/** Lo que una persona de Fundación puede hacer sobre una pieza. */
export const accionSobrePiezaSchema = z.enum([
    'editar',
    'aprobar',
    'publicar',
    'observar',
    'retirar',
]);
/**
 * Las transiciones válidas, exactamente las del prompt de PIA-1 (Cowork, 4/11):
 *
 *   pendiente -> observada | aprobada
 *   observada -> pendiente (al editar) | retirada
 *   aprobada  -> publicada | observada
 *   publicada -> observada (al editar: baja y pide aprobar de nuevo) | retirada
 *   retirada  -> pendiente (al editar)
 *
 * No hay otra. En particular, una pieza observada no se aprueba sin corregir
 * su texto, y una retirada no vuelve a publicarse sin pasar otra vez por
 * aprobar y publicar.
 */
export const TRANSICIONES_DEL_CORPUS = Object.freeze({
    pendiente_de_fundacion: ['observada', 'aprobada'],
    observada: ['pendiente_de_fundacion', 'retirada'],
    aprobada: ['publicada', 'observada'],
    publicada: ['observada', 'retirada'],
    retirada: ['pendiente_de_fundacion'],
});
export function esTransicionValida(de, a) {
    return TRANSICIONES_DEL_CORPUS[de].includes(a);
}
/**
 * El estado en que queda una pieza después de una acción, o `null` si la
 * acción no se puede hacer en ese estado.
 *
 * **Editar** es la acción que más casos tiene, porque el texto cambia y cada
 * cambio es una versión nueva:
 *
 *   - una pendiente sigue pendiente (corregir antes de aprobar no cambia nada);
 *   - una observada o una retirada vuelven a pendiente: el texto nuevo tiene
 *     que aprobarse;
 *   - una publicada baja a observada: lo que PIA decía ya no es lo aprobado, y
 *     sale de la búsqueda en el mismo momento;
 *   - una aprobada **no se edita**: lo aprobado es ese texto. Para cambiarlo se
 *     observa primero, que es una transición válida, y después se corrige.
 *
 * Las otras cuatro son una transición directa, y sólo desde los estados que
 * la permiten.
 */
const TRAS_LA_ACCION = Object.freeze({
    editar: {
        pendiente_de_fundacion: 'pendiente_de_fundacion',
        observada: 'pendiente_de_fundacion',
        retirada: 'pendiente_de_fundacion',
        publicada: 'observada',
    },
    aprobar: { pendiente_de_fundacion: 'aprobada' },
    publicar: { aprobada: 'publicada' },
    observar: { pendiente_de_fundacion: 'observada', aprobada: 'observada' },
    retirar: { observada: 'retirada', publicada: 'retirada' },
});
export function estadoTrasLaAccion(estado, accion) {
    return TRAS_LA_ACCION[accion][estado] ?? null;
}
/** Los módulos que la búsqueda de PIA puede devolver. `reglas_de_pia`, nunca. */
export const MODULOS_RECUPERABLES = Object.freeze([
    'programa',
    'educacion',
    'acompanamiento',
]);
export function moduloRecuperable(modulo) {
    return MODULOS_RECUPERABLES.includes(modulo);
}
/**
 * Si la pieza puede ser la respuesta de PIA, tal cual. Por defecto sí en los
 * tres módulos de contenido y no en las reglas, que no son para el paciente.
 */
export function habilitadaPorDefecto(modulo) {
    return moduloRecuperable(modulo);
}
// ─────────────────────────────────────────────────────────────────────────────
// Los contratos del panel "Contenidos de PIA"
// ─────────────────────────────────────────────────────────────────────────────
const codigoDePieza = z.string().regex(/^\d{2}-\d{3}$/);
const instante = z.iso.datetime();
/** Una pieza en la lista: sin el texto entero, que se lee en su ficha. */
export const filaDePiezaSchema = z.strictObject({
    id: z.uuid(),
    codigo: codigoDePieza,
    modulo: moduloDelCorpusSchema,
    tema: z.string().min(1).max(200),
    pregunta: z.string().min(1).max(300),
    estado: estadoDePiezaSchema,
    /** El nombre que escribió quien la aprobó. Nulo si nadie la aprobó. */
    aprobadaPor: z.string().min(1).max(80).nullable(),
    /** Como vino en la planilla: no es una fecha ("Hasta proxima revisión anual"). */
    validoHasta: z.string().min(1).max(80),
    version: z.number().int().positive(),
});
const cuentasPorEstado = z.strictObject({
    pendiente_de_fundacion: z.number().int().nonnegative(),
    observada: z.number().int().nonnegative(),
    aprobada: z.number().int().nonnegative(),
    publicada: z.number().int().nonnegative(),
    retirada: z.number().int().nonnegative(),
});
/** `GET /api/v1/fundacion/corpus`: las piezas de la red y las cuentas por módulo. */
export const listaDelCorpusSchema = z.strictObject({
    piezas: z.array(filaDePiezaSchema),
    /** Una entrada por módulo, en el orden de la planilla. */
    cuentas: z.array(z.strictObject({ modulo: moduloDelCorpusSchema, porEstado: cuentasPorEstado })),
});
/**
 * Por qué no se puede publicar, cuando no se puede. La doble validación pide
 * que publique una persona distinta de la que aprobó.
 */
export const motivoSinPublicarSchema = z.enum([
    /** No está aprobada. */
    'no_esta_aprobada',
    /** La aprobó la persona que está mirando. */
    'la_aprobo_usted',
    /** En la red hay una sola persona de Fundación: no hay segunda firma posible. */
    'una_sola_persona',
]);
export const versionDePiezaSchema = z.strictObject({
    version: z.number().int().positive(),
    estado: estadoDePiezaSchema,
    texto: z.string().min(1).max(4000),
    /** El alias de la cuenta de Fundación. Nulo en la carga del material. */
    quien: z.string().min(1).max(80).nullable(),
    cuando: instante,
});
/** `GET /api/v1/fundacion/corpus/:codigo`: la ficha entera de una pieza. */
export const fichaDePiezaSchema = z.strictObject({
    pieza: filaDePiezaSchema,
    etapa: z.string().min(1).max(120),
    texto: z.string().min(1).max(4000),
    fuente: z.string().min(1).max(300).nullable(),
    observacion: z.string().min(1).max(500).nullable(),
    motivoDeRetiro: z.string().min(1).max(500).nullable(),
    aprobadaAt: instante.nullable(),
    publicadaAt: instante.nullable(),
    /** Si la pieza puede ser la respuesta de PIA tal cual. */
    respuestaDirecta: z.boolean(),
    /**
     * Si tiene sus fragmentos en el índice de búsqueda. Una pieza publicada sin
     * índice no se encontraría nunca; las reglas de PIA no se indexan.
     */
    indexada: z.boolean(),
    historial: z.array(versionDePiezaSchema),
    /** Qué se puede hacer desde acá, decidido por la api con la misma regla que valida. */
    acciones: z.strictObject({
        editar: z.boolean(),
        aprobar: z.boolean(),
        publicar: z.boolean(),
        sinPublicarPorque: motivoSinPublicarSchema.nullable(),
        observar: z.boolean(),
        retirar: z.boolean(),
    }),
});
export const parametrosDePiezaSchema = z.strictObject({ codigo: codigoDePieza });
/**
 * `POST /api/v1/fundacion/corpus/:codigo/acciones`: una acción por pedido.
 *
 * El nombre de quien aprueba es obligatorio: queda escrito en la pieza. La
 * observación y el motivo del retiro, también: una pieza no se observa ni se
 * retira sin decir por qué.
 */
export const accionDePiezaSchema = z.discriminatedUnion('accion', [
    z.strictObject({ accion: z.literal('editar'), texto: z.string().trim().min(1).max(4000) }),
    z.strictObject({ accion: z.literal('aprobar'), nombre: z.string().trim().min(3).max(80) }),
    z.strictObject({ accion: z.literal('publicar') }),
    z.strictObject({
        accion: z.literal('observar'),
        observacion: z.string().trim().min(3).max(500),
    }),
    z.strictObject({ accion: z.literal('retirar'), motivo: z.string().trim().min(3).max(500) }),
]);
/** La respuesta de toda acción: la ficha como quedó. */
export const resultadoDeAccionSchema = fichaDePiezaSchema;
//# sourceMappingURL=corpus.js.map